import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Page, Layout, Card, FormLayout, TextField, Select, Checkbox, BlockStack,
  Text, InlineStack, Banner, Spinner, Box, Badge, Toast,
} from '@shopify/polaris';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getTire, updateTire, type TireProductFull, type TirePatch } from '../api/tires';
import {
  ListingSeoPanel, GoogleMerchantPanel, MetafieldsPanel, VariantsPanel,
} from '../components/ListingPanels';

function dollars(cents: number | null | undefined): string {
  if (cents == null) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

// 175-65-15 → 175/65-15. Handle stays hyphenated for URL safety.
function formatSizeDisplay(sizeFull: string | null | undefined): string {
  if (!sizeFull) return '';
  const s = String(sizeFull);
  if (/^\d{2}(?:\.\d+)?x\d{1,2}(?:\.\d+)?r\d{2}$/i.test(s)) return s.toUpperCase();
  const m = s.match(/^(p|lt|st)?(\d{3})-(\d{2})-(\d{2})$/i);
  if (m) {
    const prefix = (m[1] ?? '').toUpperCase();
    return `${prefix}${m[2]}/${m[3]}-${m[4]}`;
  }
  return s;
}

const INSTALL_LABEL: Record<string, string> = {
  same_day: 'Same day',
  next_day: '1-3 days out',
  standard: '3-5 days out',
  estimated: 'Estimated',
  out_of_stock: 'Out of stock',
};

const SOURCE_LABEL: Record<string, string> = {
  atd: 'From ATD',
  wheelpros: 'From Wheel Pros',
  inferred_model: 'Inferred (curated model map)',
  inferred_pattern: 'Inferred (model name keyword)',
  inferred_category: 'Inferred (productGroup category)',
  inferred_size: 'Inferred (size + load_range)',
  admin: 'Set by admin',
};

const DESCRIPTION_SOURCE_LABEL: Record<string, string> = {
  manufacturer_site: 'Manufacturer site (admin-curated)',
  wheelpros_structured: 'Wheel Pros narrative',
  manufacturer: 'Manufacturer copy',
  atd_structured: 'ATD structured descriptor',
  structured_facts: 'Structured facts only',
  admin: 'Edited by admin',
  blank: 'No description',
};

function ago(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function qty(n: number | null | undefined): string {
  if (n == null) return '—';
  return String(n);
}

export function TireDetail() {
  const { id } = useParams<{ id: string }>();
  const numericId = id ? parseInt(id, 10) : null;
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: tire, isLoading, error } = useQuery({
    queryKey: ['tire', numericId],
    queryFn: () => getTire(numericId!),
    enabled: numericId !== null && Number.isFinite(numericId),
  });

  const [form, setForm] = useState<TirePatch>({});
  const [toast, setToast] = useState<string | null>(null);

  // Reset form when tire id changes (otherwise edits to one row leak into another).
  useEffect(() => { setForm({}); }, [numericId]);

  const save = useMutation({
    mutationFn: (patch: TirePatch) => updateTire(numericId!, patch),
    onSuccess: () => {
      setToast('Saved');
      setForm({});
      qc.invalidateQueries({ queryKey: ['tire', numericId] });
      qc.invalidateQueries({ queryKey: ['tires'] });
      qc.invalidateQueries({ queryKey: ['tire-stats'] });
    },
    onError: (err: Error) => setToast(`Save failed: ${err.message}`),
  });

  if (isLoading) return <Page title="Tire"><Spinner /></Page>;
  if (error) return <Page title="Tire"><Banner tone="critical">{(error as Error).message}</Banner></Page>;
  if (!tire) return <Page title="Tire"><Banner>Not found</Banner></Page>;

  // The displayed value for any field: pending edit overrides the saved value.
  // Returns `any` deliberately — call sites narrow with explicit casts. The
  // alternative (a proper generic returning `TirePatch[K]`) had TSC inferring
  // the JSX subtree as 'unknown' for reasons that aren't worth fighting.
  const v = (key: keyof TirePatch): any =>
    (form[key] !== undefined ? form[key] : (tire as any)[key]);

  const setField = <K extends keyof TirePatch>(key: K) => (value: TirePatch[K]) =>
    setForm((p) => ({ ...p, [key]: value }));

  const dirty = Object.keys(form).length > 0;

  return (
    <>
      <Page
        title={`${tire.brand} ${tire.model} ${formatSizeDisplay(tire.tire_size_full)} Tire`}
        subtitle={`${tire.vendor_sku} · ${formatSizeDisplay(tire.tire_size_full)}`}
        backAction={{ content: 'Tires', onAction: () => navigate('/tires') }}
        primaryAction={{
          content: 'Save changes',
          loading: save.isPending,
          disabled: !dirty || save.isPending,
          onAction: () => save.mutate(form),
        }}
      >
        {save.error && (
          <Box paddingBlockEnd="400">
            <Banner tone="critical" title="Save failed">{(save.error as Error).message}</Banner>
          </Box>
        )}
        {tire.needs_review && (
          <Box paddingBlockEnd="400">
            <Banner tone="warning" title="Needs review">
              <Text as="p" variant="bodySm">
                {(tire.needs_review_reasons ?? []).join(', ') || 'No reason listed'}
              </Text>
            </Banner>
          </Box>
        )}

        <Layout>
          {/* Identity ----------------------------------------------------- */}
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="h3" variant="headingMd">Identity</Text>
                  <InlineStack gap="200">
                    <Badge tone="info">{tire.vendor}</Badge>
                    {!tire.is_active && <Badge tone="critical">Inactive</Badge>}
                  </InlineStack>
                </InlineStack>
                <FormLayout>
                  <FormLayout.Group>
                    <TextField label="Vendor SKU" value={tire.vendor_sku} disabled autoComplete="off" />
                    <TextField label="Brand" value={tire.brand} disabled autoComplete="off" />
                    <TextField label="Model" value={tire.model} disabled autoComplete="off" />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <TextField label="Handle (URL slug)" value={tire.handle} disabled autoComplete="off" />
                    <TextField label="UPC" value={tire.upc ?? ''} disabled autoComplete="off" />
                    <TextField label="Brand slug" value={tire.brand_slug} disabled autoComplete="off" />
                  </FormLayout.Group>
                </FormLayout>
                <Text as="p" variant="bodySm" tone="subdued">
                  Identity columns are the idempotency key — sourced from the vendor and not editable here.
                </Text>
              </BlockStack>
            </Card>
          </Layout.Section>

          {/* Size --------------------------------------------------------- */}
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">Size</Text>
                <InlineStack gap="400" wrap>
                  <Box minWidth="180px">
                    <Text as="p" variant="bodySm" tone="subdued">Display</Text>
                    <Text as="p" variant="headingMd">{formatSizeDisplay(tire.tire_size_full)}</Text>
                  </Box>
                  <Box minWidth="180px">
                    <Text as="p" variant="bodySm" tone="subdued">Canonical (DB / handle)</Text>
                    <Text as="p" variant="bodyMd"><code>{tire.tire_size_full}</code></Text>
                  </Box>
                  <Box minWidth="120px">
                    <Text as="p" variant="bodySm" tone="subdued">Format</Text>
                    <Text as="p" variant="bodyMd">{tire.size_format}</Text>
                  </Box>
                  <Box minWidth="120px">
                    <Text as="p" variant="bodySm" tone="subdued">Section width</Text>
                    <Text as="p" variant="bodyMd">{tire.section_width ?? '—'}</Text>
                  </Box>
                  <Box minWidth="120px">
                    <Text as="p" variant="bodySm" tone="subdued">Aspect ratio</Text>
                    <Text as="p" variant="bodyMd">{tire.aspect_ratio ?? '—'}</Text>
                  </Box>
                  <Box minWidth="120px">
                    <Text as="p" variant="bodySm" tone="subdued">Rim diameter</Text>
                    <Text as="p" variant="bodyMd">{tire.rim_diameter}"</Text>
                  </Box>
                  <Box minWidth="160px">
                    <Text as="p" variant="bodySm" tone="subdued">Overall diameter</Text>
                    <Text as="p" variant="bodyMd">{tire.overall_diameter ? `${tire.overall_diameter}"` : '—'}</Text>
                  </Box>
                </InlineStack>
              </BlockStack>
            </Card>
          </Layout.Section>

          {/* Spec --------------------------------------------------------- */}
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">Spec & classification</Text>
                <FormLayout>
                  <FormLayout.Group>
                    <Select
                      label="Terrain"
                      options={[
                        { label: '—', value: '' },
                        { label: 'Highway / Touring (HT)', value: 'ht' },
                        { label: 'All-Terrain (AT)', value: 'at' },
                        { label: 'Rugged-Terrain (RT)', value: 'rt' },
                        { label: 'Mud-Terrain (MT)', value: 'mt' },
                      ]}
                      value={(v('terrain') as string) ?? ''}
                      onChange={(val) => setField('terrain')(val === '' ? null : (val as 'mt' | 'rt' | 'at' | 'ht'))}
                    />
                    <Select
                      label="Vehicle type"
                      options={[
                        { label: '—', value: '' },
                        { label: 'Truck', value: 'truck' },
                        { label: 'SUV', value: 'suv' },
                        { label: 'Car', value: 'car' },
                        { label: 'Off-road', value: 'offroad' },
                      ]}
                      value={(v('vehicle_type') as string) ?? ''}
                      onChange={(val) => setField('vehicle_type')(val === '' ? null : (val as 'truck' | 'suv' | 'car' | 'offroad'))}
                    />
                    <Select
                      label="Season"
                      options={[
                        { label: '—', value: '' },
                        { label: 'All-season', value: 'all-season' },
                        { label: 'Summer', value: 'summer' },
                        { label: 'Winter', value: 'winter' },
                      ]}
                      value={(v('season') as string) ?? ''}
                      onChange={(val) => setField('season')(val === '' ? null : (val as 'all-season' | 'summer' | 'winter'))}
                    />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <TextField label="Sidewall" value={(v('sidewall') as string) ?? ''} onChange={(val) => setField('sidewall')(val || null)} autoComplete="off" />
                    <TextField label="Load range" value={tire.load_range ?? ''} disabled autoComplete="off" />
                    <TextField label="Ply rating" value={tire.ply_rating != null ? String(tire.ply_rating) : ''} disabled autoComplete="off" />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <TextField label="Load index" value={tire.load_index ?? ''} disabled autoComplete="off" />
                    <TextField label="Speed rating" value={tire.speed_rating ?? ''} disabled autoComplete="off" />
                    <Checkbox label="Run-flat" checked={(v('run_flat') as boolean) ?? false} onChange={(c) => setField('run_flat')(c)} />
                    <Checkbox label="Studdable" checked={(v('studdable') as boolean) ?? false} onChange={(c) => setField('studdable')(c)} />
                  </FormLayout.Group>
                </FormLayout>
                <Text as="p" variant="bodySm" tone="subdued">
                  Terrain & vehicle type are the curate-only classification — when you set both, "needs review" auto-clears. Load range / ply / load index / speed rating come straight from ATD and are read-only here.
                </Text>
              </BlockStack>
            </Card>
          </Layout.Section>

          {/* Pricing ------------------------------------------------------ */}
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">Pricing</Text>
                <InlineStack gap="400" wrap>
                  <Box minWidth="140px">
                    <Text as="p" variant="bodySm" tone="subdued">Sell price</Text>
                    <Text as="p" variant="headingLg">{dollars(tire.price_cents)}</Text>
                  </Box>
                  <Box minWidth="120px">
                    <Text as="p" variant="bodySm" tone="subdued">MSRP</Text>
                    <Text as="p" variant="headingMd">{dollars(tire.msrp_cents)}</Text>
                  </Box>
                  <Box minWidth="120px">
                    <Text as="p" variant="bodySm" tone="subdued">Cost</Text>
                    <Text as="p" variant="headingMd">{dollars(tire.cost_cents)}</Text>
                  </Box>
                  <Box minWidth="120px">
                    <Text as="p" variant="bodySm" tone="subdued">FET</Text>
                    <Text as="p" variant="headingMd">{dollars(tire.fet_cents)}</Text>
                  </Box>
                </InlineStack>
                <Text as="p" variant="bodySm" tone="subdued">
                  Cost is dealer-only — never written to Shopify. FET (federal excise tax) is the per-tire add-on for medium-truck SKUs.
                </Text>
              </BlockStack>
            </Card>
          </Layout.Section>

          {/* Inventory ---------------------------------------------------- */}
          {/* When a tire row is sourced from one vendor (ATD primary)
              and enriched by another (WheelPros), the operator wants
              to see BOTH sources side-by-side: which vendor ships the
              qty data and which only contributes specs. ATD owns the
              inventory; WP, when present, contributes specs/MAP/
              description but no qty (catalog-only on this account). */}
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">Inventory · by source</Text>

                {/* ATD inventory block — always shown for ATD-primary rows */}
                {tire.vendor === 'ATD' && (
                  <Box background="bg-surface-secondary" padding="300" borderRadius="200">
                    <BlockStack gap="200">
                      <InlineStack gap="200" blockAlign="center">
                        <Badge tone="info">ATD</Badge>
                        <Text as="span" variant="bodySm" tone="subdued">
                          Primary inventory · SKU {tire.vendor_sku}
                        </Text>
                      </InlineStack>
                      <InlineStack gap="400" wrap>
                        <Box minWidth="180px">
                          <Text as="p" variant="bodySm" tone="subdued">Install speed</Text>
                          <Box paddingBlockStart="100">
                            <InstallSpeedBadge speed={tire.install_speed} />
                          </Box>
                        </Box>
                        <Box minWidth="140px">
                          <Text as="p" variant="bodySm" tone="subdued">Same-day (DC 121)</Text>
                          <Text as="p" variant="headingMd">{qty(tire.qty_local)}</Text>
                        </Box>
                        <Box minWidth="140px">
                          <Text as="p" variant="bodySm" tone="subdued">1-3 days out (Local Plus)</Text>
                          <Text as="p" variant="headingMd">{qty(tire.qty_local_plus)}</Text>
                        </Box>
                        <Box minWidth="140px">
                          <Text as="p" variant="bodySm" tone="subdued">3-5 days out (other DCs)</Text>
                          <Text as="p" variant="headingMd">{qty(tire.qty_nationwide)}</Text>
                        </Box>
                        <Box minWidth="140px">
                          <Text as="p" variant="bodySm" tone="subdued">Total ATD</Text>
                          <Text as="p" variant="headingMd">
                            {((tire.qty_local ?? 0) + (tire.qty_local_plus ?? 0) + (tire.qty_nationwide ?? 0)).toLocaleString()}
                          </Text>
                        </Box>
                      </InlineStack>
                    </BlockStack>
                  </Box>
                )}

                {/* WheelPros enrichment block — shown ONLY when WP enriched
                    an ATD-primary row (i.e. tire.vendor === 'ATD' AND
                    enriched_by_vendors includes 'WheelPros'). WP-primary
                    rows always have 'WheelPros' in enriched_by_vendors
                    by construction; rendering this block AND the
                    WP-only block below for those rows would describe
                    the same vendor twice.

                    Inventory layout mirrors the ATD block (Install /
                    Same-day / 1-3 days / 3-5 days / Total) so the
                    operator can compare side-by-side. WP catalog SFTP
                    on this account is catalog-only — no qty data —
                    so each cell renders "—" with a subdued caption.
                    When (later) we wire a WP inventory source for
                    tires, this same layout will populate from those
                    columns with no UI change. */}
                {tire.vendor !== 'WheelPros' && (tire.enriched_by_vendors ?? []).includes('WheelPros') && (
                  <Box background="bg-surface-secondary" padding="300" borderRadius="200">
                    <BlockStack gap="200">
                      <InlineStack gap="200" blockAlign="center">
                        <Badge tone="success">Wheel Pros</Badge>
                        <Text as="span" variant="bodySm" tone="subdued">
                          Spec enrichment · no per-warehouse qty on this account
                        </Text>
                      </InlineStack>
                      <InlineStack gap="400" wrap>
                        <Box minWidth="180px">
                          <Text as="p" variant="bodySm" tone="subdued">Install speed</Text>
                          <Box paddingBlockStart="100">
                            <Badge>Not provided</Badge>
                          </Box>
                        </Box>
                        <Box minWidth="140px">
                          <Text as="p" variant="bodySm" tone="subdued">Same day</Text>
                          <Text as="p" variant="headingMd" tone="subdued">—</Text>
                        </Box>
                        <Box minWidth="140px">
                          <Text as="p" variant="bodySm" tone="subdued">1-3 days out</Text>
                          <Text as="p" variant="headingMd" tone="subdued">—</Text>
                        </Box>
                        <Box minWidth="140px">
                          <Text as="p" variant="bodySm" tone="subdued">3-5 days out</Text>
                          <Text as="p" variant="headingMd" tone="subdued">—</Text>
                        </Box>
                        <Box minWidth="140px">
                          <Text as="p" variant="bodySm" tone="subdued">Total WP</Text>
                          <Text as="p" variant="headingMd" tone="subdued">—</Text>
                        </Box>
                      </InlineStack>
                      <Text as="p" variant="bodySm" tone="subdued">
                        Wheel Pros confirmed this product on the catalog feed and contributed
                        manufacturer specs (UTQG, max PSI, mileage warranty, country of origin,
                        rim-width range) when ATD didn't ship them. Wheel Pros's tire-catalog
                        SFTP account doesn't expose per-warehouse qty, so customer-facing
                        availability stays driven by the ATD block above.
                      </Text>
                      {tire.map_price_cents != null && (
                        <Text as="p" variant="bodySm" tone="subdued">
                          MAP enforced from Wheel Pros: ${(tire.map_price_cents / 100).toFixed(2)}
                        </Text>
                      )}
                    </BlockStack>
                  </Box>
                )}

                {/* WheelPros-primary row (no ATD inventory at all) — the
                    1,582 standalone WP tire rows from the enrichment
                    pull. Same layout as ATD/WP-enrichment so a single
                    visual scan answers "what's in stock where?". WP
                    tire-catalog SFTP doesn't ship qty so the cells
                    show 0 / — with the OOS framing below. */}
                {tire.vendor === 'WheelPros' && (
                  <Box background="bg-surface-secondary" padding="300" borderRadius="200">
                    <BlockStack gap="200">
                      <InlineStack gap="200" blockAlign="center">
                        <Badge tone="warning">Wheel Pros only</Badge>
                        <Text as="span" variant="bodySm" tone="subdued">
                          ATD doesn't ship this tire · {tire.vendor_sku}
                        </Text>
                      </InlineStack>
                      <InlineStack gap="400" wrap>
                        <Box minWidth="180px">
                          <Text as="p" variant="bodySm" tone="subdued">Install speed</Text>
                          <Box paddingBlockStart="100">
                            <InstallSpeedBadge speed={tire.install_speed} />
                          </Box>
                        </Box>
                        <Box minWidth="140px">
                          <Text as="p" variant="bodySm" tone="subdued">Same day</Text>
                          <Text as="p" variant="headingMd" tone="subdued">{qty(tire.qty_local)}</Text>
                        </Box>
                        <Box minWidth="140px">
                          <Text as="p" variant="bodySm" tone="subdued">1-3 days out</Text>
                          <Text as="p" variant="headingMd" tone="subdued">{qty(tire.qty_local_plus)}</Text>
                        </Box>
                        <Box minWidth="140px">
                          <Text as="p" variant="bodySm" tone="subdued">3-5 days out</Text>
                          <Text as="p" variant="headingMd" tone="subdued">{qty(tire.qty_nationwide)}</Text>
                        </Box>
                        <Box minWidth="140px">
                          <Text as="p" variant="bodySm" tone="subdued">Total WP</Text>
                          <Text as="p" variant="headingMd" tone="subdued">
                            {((tire.qty_local ?? 0) + (tire.qty_local_plus ?? 0) + (tire.qty_nationwide ?? 0)) || '—'}
                          </Text>
                        </Box>
                      </InlineStack>
                      <Text as="p" variant="bodySm">
                        Wheel Pros carries this SKU but there's no inventory data on the catalog
                        feed and no matching ATD row to source qty from. Treated as out-of-stock
                        until a Wheel Pros inventory pipeline is wired or an admin curates qty.
                      </Text>
                    </BlockStack>
                  </Box>
                )}

                <Text as="p" variant="bodySm" tone="subdued">
                  ATD's getProductByCriteria SOAP method returns three pre-bucketed counts keyed off our dealer locationNumber 914759. Local = stock at DC 121 (Kennesaw GA) — same-day install confirmed. Local Plus = nearby regional warehouses. Nationwide = all other ATD warehouses. ATD does not expose per-warehouse counts via this SOAP method, so we treat regional/nationwide as availability tiers rather than concrete delivery dates. NULL = vendor didn't ship the field; 0 = confirmed empty.
                </Text>
              </BlockStack>
            </Card>
          </Layout.Section>

          {/* Image -------------------------------------------------------- */}
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">Image</Text>
                <FormLayout>
                  <TextField
                    label="Image URL"
                    value={(v('image_url') as string) ?? ''}
                    onChange={(val) => setField('image_url')(val || null)}
                    autoComplete="off"
                    helpText="Vendor-provided URL. Leave blank if missing — the storefront falls back to a placeholder."
                  />
                </FormLayout>
                {(v('image_url') as string) && (
                  <Box>
                    <img
                      src={(v('image_url') as string) ?? ''}
                      alt={`${tire.brand} ${tire.model}`}
                      style={{ maxHeight: 240, maxWidth: '100%', objectFit: 'contain', background: '#f6f6f7', borderRadius: 8 }}
                    />
                  </Box>
                )}
              </BlockStack>
            </Card>
          </Layout.Section>

          {/* Audit / source ---------------------------------------------- */}
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">Source & audit</Text>
                <InlineStack gap="400" wrap>
                  <Box minWidth="180px">
                    <Text as="p" variant="bodySm" tone="subdued">Imported</Text>
                    <Text as="p" variant="bodyMd">{ago(tire.imported_at)}</Text>
                  </Box>
                  <Box minWidth="180px">
                    <Text as="p" variant="bodySm" tone="subdued">Last updated</Text>
                    <Text as="p" variant="bodyMd">{ago(tire.updated_at)}</Text>
                  </Box>
                  <Box minWidth="180px">
                    <Text as="p" variant="bodySm" tone="subdued">Source job</Text>
                    <Text as="p" variant="bodyMd">{tire.source_job_id ?? '—'}</Text>
                  </Box>
                </InlineStack>
                <FormLayout>
                  <FormLayout.Group>
                    <Checkbox
                      label="Active (visible on storefront)"
                      checked={(v('is_active') as boolean) ?? true}
                      onChange={(c) => setField('is_active')(c)}
                    />
                    <Checkbox
                      label="Needs review"
                      checked={(v('needs_review') as boolean) ?? false}
                      onChange={(c) => setField('needs_review')(c)}
                      helpText="Auto-clears when terrain + vehicle_type are both set."
                    />
                  </FormLayout.Group>
                </FormLayout>
              </BlockStack>
            </Card>
          </Layout.Section>

          {/* Mapping sources ------------------------------------------- */}
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">Mapping sources</Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Where each curated value came from. Inferred values can be overridden by editing the field above — once an admin saves an edit, the source flips to "Set by admin" and future re-imports won't overwrite it.
                </Text>
                <InlineStack gap="400" wrap>
                  <Box minWidth="220px">
                    <Text as="p" variant="bodySm" tone="subdued">Terrain source</Text>
                    <Text as="p" variant="bodyMd">
                      {tire.terrain_source ? (SOURCE_LABEL[tire.terrain_source] ?? tire.terrain_source) : '— (not set)'}
                    </Text>
                  </Box>
                  <Box minWidth="220px">
                    <Text as="p" variant="bodySm" tone="subdued">Vehicle type source</Text>
                    <Text as="p" variant="bodyMd">
                      {tire.vehicle_type_source ? (SOURCE_LABEL[tire.vehicle_type_source] ?? tire.vehicle_type_source) : '— (not set)'}
                    </Text>
                  </Box>
                  <Box minWidth="220px">
                    <Text as="p" variant="bodySm" tone="subdued">Description source</Text>
                    <Text as="p" variant="bodyMd">
                      {tire.description_source ? (DESCRIPTION_SOURCE_LABEL[tire.description_source] ?? tire.description_source) : '—'}
                    </Text>
                  </Box>
                </InlineStack>
              </BlockStack>
            </Card>
          </Layout.Section>

          {/* Data sources ----------------------------------------------- */}
          {/* Surfaces which vendors contributed to this row. Inventory is
              always ATD (qty + install_speed). Specs may be enriched by
              Wheel Pros or other vendors via enriched_by_vendors. */}
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">Data sources</Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Inventory comes from ATD (qty buckets + install timing). Specs may be enriched by other vendors when they ship richer data — ATD values are never overwritten; new fields are filled into gaps.
                </Text>
                <InlineStack gap="400" wrap>
                  <Box minWidth="220px">
                    <Text as="p" variant="bodySm" tone="subdued">Inventory</Text>
                    <InlineStack gap="100">
                      <Badge tone="info">ATD</Badge>
                    </InlineStack>
                    <Text as="p" variant="bodySm" tone="subdued">
                      ATD's 3-bucket rollup (Local / Local Plus / Nationwide). Wheel Pros has no inventory data on this account, so qty stays ATD-driven.
                    </Text>
                  </Box>
                  <Box minWidth="260px">
                    <Text as="p" variant="bodySm" tone="subdued">Specs enriched by</Text>
                    <InlineStack gap="100" wrap>
                      {(tire.enriched_by_vendors ?? []).length === 0 ? (
                        <Text as="span" tone="subdued">— (vendor only)</Text>
                      ) : (
                        (tire.enriched_by_vendors ?? []).map((v) => (
                          <Badge key={v} tone={v === 'WheelPros' ? 'success' : 'info'}>{v}</Badge>
                        ))
                      )}
                    </InlineStack>
                  </Box>
                  <Box minWidth="220px">
                    <Text as="p" variant="bodySm" tone="subdued">Description source</Text>
                    <Text as="p" variant="bodyMd">
                      {tire.description_source ? (DESCRIPTION_SOURCE_LABEL[tire.description_source] ?? tire.description_source) : '—'}
                    </Text>
                  </Box>
                </InlineStack>

                {/* Highlight reel — fields admin most often wants visible
                    when WP enrichment lands. Each is rendered only when
                    populated; nothing is invented. */}
                {(tire.mileage_warranty || tire.utqg || tire.max_psi != null || tire.min_rim_width != null) && (
                  <InlineStack gap="400" wrap>
                    {tire.mileage_warranty && (
                      <Box minWidth="200px">
                        <Text as="p" variant="bodySm" tone="subdued">Mileage warranty</Text>
                        <Text as="p" variant="headingSm">{tire.mileage_warranty}</Text>
                      </Box>
                    )}
                    {tire.utqg && (
                      <Box minWidth="200px">
                        <Text as="p" variant="bodySm" tone="subdued">UTQG</Text>
                        <Text as="p" variant="headingSm">
                          {tire.utqg}
                          {(tire.traction_rating || tire.temperature_rating) && (
                            <Text as="span" tone="subdued"> ({tire.traction_rating ?? '—'} / {tire.temperature_rating ?? '—'})</Text>
                          )}
                        </Text>
                      </Box>
                    )}
                    {tire.max_psi != null && (
                      <Box minWidth="160px">
                        <Text as="p" variant="bodySm" tone="subdued">Max PSI</Text>
                        <Text as="p" variant="headingSm">{tire.max_psi} psi</Text>
                      </Box>
                    )}
                    {(tire.min_rim_width != null || tire.max_rim_width != null) && (
                      <Box minWidth="220px">
                        <Text as="p" variant="bodySm" tone="subdued">Rim width range</Text>
                        <Text as="p" variant="headingSm">
                          {tire.min_rim_width ?? '—'}″ – {tire.max_rim_width ?? '—'}″
                        </Text>
                      </Box>
                    )}
                  </InlineStack>
                )}

                {/* Wheel Pros narrative description (vendor_description) —
                    distinct from ATD's structured descriptor; only shown
                    when the WP pull populated it. */}
                {tire.vendor_description && (
                  <Box>
                    <Text as="p" variant="bodySm" tone="subdued">Vendor narrative description (Wheel Pros)</Text>
                    <Box background="bg-surface-secondary" padding="300" borderRadius="200">
                      <Text as="p" variant="bodyMd">{tire.vendor_description}</Text>
                    </Box>
                  </Box>
                )}
              </BlockStack>
            </Card>
          </Layout.Section>

          {/* Manufacturer data ----------------------------------------- */}
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">Manufacturer data</Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Vendor-shipped fields preserved verbatim. Used for SEO, filters, and the structured-facts blurb. Read-only — to change, edit at the vendor.
                </Text>
                <InlineStack gap="400" wrap>
                  <Box minWidth="220px">
                    <Text as="p" variant="bodySm" tone="subdued">Manufacturer part #</Text>
                    <Text as="p" variant="bodyMd">{tire.mfg_part_number ?? '—'}</Text>
                  </Box>
                  <Box minWidth="220px">
                    <Text as="p" variant="bodySm" tone="subdued">Mileage warranty</Text>
                    <Text as="p" variant="bodyMd">{tire.mileage_warranty ?? '—'}</Text>
                  </Box>
                  <Box minWidth="180px">
                    <Text as="p" variant="bodySm" tone="subdued">Tread depth</Text>
                    <Text as="p" variant="bodyMd">{tire.tread_depth != null ? `${tire.tread_depth}/32"` : '—'}</Text>
                  </Box>
                  <Box minWidth="180px">
                    <Text as="p" variant="bodySm" tone="subdued">UTQG treadwear</Text>
                    <Text as="p" variant="bodyMd">{tire.tread_wear ?? '—'}</Text>
                  </Box>
                  <Box minWidth="220px">
                    <Text as="p" variant="bodySm" tone="subdued">Overall tire diameter</Text>
                    <Text as="p" variant="bodyMd">{tire.overall_tire_diameter ? `${tire.overall_tire_diameter}"` : '—'}</Text>
                  </Box>
                </InlineStack>
                {tire.atd_description && (
                  <Box>
                    <Text as="p" variant="bodySm" tone="subdued">Manufacturer description (raw from ATD)</Text>
                    <Box background="bg-surface-secondary" padding="300" borderRadius="200">
                      <Text as="p" variant="bodyMd">{tire.atd_description}</Text>
                    </Box>
                  </Box>
                )}
              </BlockStack>
            </Card>
          </Layout.Section>

          {/* ATD Full Specs ---------------------------------------------- */}
          {/* Every populated field ATD ships, beyond the headline spec block.
              UTQG, max PSI, load, rim-width compatibility range, country of
              origin, RPM, shipping weight, image gallery. All read-only.
              Anything ATD didn't ship for this SKU shows as —. */}
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text as="h3" variant="headingMd">ATD full specs</Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Vendor-shipped technical fields preserved verbatim from ATD's productSpec. Used for SEO metafields, storefront filters, and the structured-facts blurb.
                </Text>
                <InlineStack gap="400" wrap>
                  <Box minWidth="180px">
                    <Text as="p" variant="bodySm" tone="subdued">UTQG</Text>
                    <Text as="p" variant="headingMd">{tire.utqg ?? '—'}</Text>
                    {(tire.traction_rating || tire.temperature_rating) && (
                      <Text as="p" variant="bodySm" tone="subdued">
                        Traction {tire.traction_rating ?? '—'} · Temp {tire.temperature_rating ?? '—'} · Tread wear {tire.tread_wear ?? '—'}
                      </Text>
                    )}
                  </Box>
                  <Box minWidth="160px">
                    <Text as="p" variant="bodySm" tone="subdued">Max PSI</Text>
                    <Text as="p" variant="bodyMd">{tire.max_psi ? `${tire.max_psi} psi` : '—'}</Text>
                  </Box>
                  <Box minWidth="180px">
                    <Text as="p" variant="bodySm" tone="subdued">Max load</Text>
                    <Text as="p" variant="bodyMd">{tire.max_load_lbs ? `${tire.max_load_lbs.toLocaleString()} lbs` : '—'}</Text>
                  </Box>
                  <Box minWidth="200px">
                    <Text as="p" variant="bodySm" tone="subdued">Rim width range</Text>
                    <Text as="p" variant="bodyMd">
                      {(tire.min_rim_width != null || tire.max_rim_width != null)
                        ? `${tire.min_rim_width ?? '?'}" – ${tire.max_rim_width ?? '?'}"`
                        : '—'}
                    </Text>
                  </Box>
                  <Box minWidth="160px">
                    <Text as="p" variant="bodySm" tone="subdued">RPM (revs / mile)</Text>
                    <Text as="p" variant="bodyMd">{tire.revolutions_per_mile ?? '—'}</Text>
                  </Box>
                  <Box minWidth="180px">
                    <Text as="p" variant="bodySm" tone="subdued">Shipping weight</Text>
                    <Text as="p" variant="bodyMd">{tire.shipping_weight_lbs ? `${tire.shipping_weight_lbs} lbs` : '—'}</Text>
                  </Box>
                  <Box minWidth="160px">
                    <Text as="p" variant="bodySm" tone="subdued">Construction</Text>
                    <Text as="p" variant="bodyMd">{tire.radial_bias === 'R' ? 'Radial' : tire.radial_bias === 'B' ? 'Bias-ply' : '—'}</Text>
                  </Box>
                  <Box minWidth="180px">
                    <Text as="p" variant="bodySm" tone="subdued">Country of origin</Text>
                    <Text as="p" variant="bodyMd">{tire.country_of_origin ?? '—'}</Text>
                  </Box>
                  <Box minWidth="200px">
                    <Text as="p" variant="bodySm" tone="subdued">Canadian warranty</Text>
                    <Text as="p" variant="bodyMd">{tire.canadian_mileage_warranty ?? '—'}</Text>
                  </Box>
                </InlineStack>
                {(tire.atd_short_description || tire.atd_long_description) && (
                  <BlockStack gap="100">
                    {tire.atd_short_description && (
                      <Box>
                        <Text as="p" variant="bodySm" tone="subdued">ATD ShortDescription</Text>
                        <Text as="p" variant="bodyMd"><code>{tire.atd_short_description}</code></Text>
                      </Box>
                    )}
                    {tire.atd_long_description && tire.atd_long_description !== tire.atd_short_description && (
                      <Box>
                        <Text as="p" variant="bodySm" tone="subdued">ATD LongDescription</Text>
                        <Text as="p" variant="bodyMd"><code>{tire.atd_long_description}</code></Text>
                      </Box>
                    )}
                  </BlockStack>
                )}
                {tire.image_gallery_json && (tire.image_gallery_json.large?.length ?? 0) > 0 && (
                  <BlockStack gap="200">
                    <Text as="p" variant="bodySm" tone="subdued">
                      Image gallery — {tire.image_gallery_json.large.length} large
                      {tire.image_gallery_json.small?.length ? ` · ${tire.image_gallery_json.small.length} small` : ''}
                      {tire.image_gallery_json.thumbnail?.length ? ` · ${tire.image_gallery_json.thumbnail.length} thumbnail` : ''}
                    </Text>
                    <InlineStack gap="200" wrap>
                      {tire.image_gallery_json.large.map((url, i) => (
                        <Box key={url} minWidth="160px">
                          <img
                            src={url}
                            alt={`${tire.brand} ${tire.model} #${i + 1}`}
                            style={{ width: 160, height: 160, objectFit: 'contain', background: '#f6f6f7', borderRadius: 8 }}
                          />
                        </Box>
                      ))}
                    </InlineStack>
                  </BlockStack>
                )}
              </BlockStack>
            </Card>
          </Layout.Section>

          {/* Shopify preview ------------------------------------------- */}
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="h3" variant="headingMd">Shopify preview</Text>
                  <Badge tone="info">not yet pushed</Badge>
                </InlineStack>
                <Text as="p" variant="bodySm" tone="subdued">
                  Generated locally from the fields above. Refreshes every time apply runs. No Shopify API call has happened yet — these values are precomputed and ready for a future dry-run/push.
                </Text>
                <BlockStack gap="200">
                  <Box>
                    <Text as="p" variant="bodySm" tone="subdued">Shopify title</Text>
                    <Text as="p" variant="bodyMd">{tire.shopify_title ?? '—'}</Text>
                  </Box>
                  <InlineStack gap="400" wrap>
                    <Box minWidth="280px">
                      <Text as="p" variant="bodySm" tone="subdued">Handle</Text>
                      <Text as="p" variant="bodyMd"><code>{tire.shopify_handle ?? tire.handle}</code></Text>
                    </Box>
                    <Box minWidth="200px">
                      <Text as="p" variant="bodySm" tone="subdued">SEO title length</Text>
                      <Text as="p" variant="bodyMd">{tire.seo_title?.length ?? 0} / 65</Text>
                    </Box>
                    <Box minWidth="200px">
                      <Text as="p" variant="bodySm" tone="subdued">SEO description length</Text>
                      <Text as="p" variant="bodyMd">{tire.seo_description?.length ?? 0} / 160</Text>
                    </Box>
                  </InlineStack>
                  <Box>
                    <Text as="p" variant="bodySm" tone="subdued">SEO title</Text>
                    <Text as="p" variant="bodyMd">{tire.seo_title ?? '—'}</Text>
                  </Box>
                  <Box>
                    <Text as="p" variant="bodySm" tone="subdued">SEO description</Text>
                    <Text as="p" variant="bodyMd">{tire.seo_description ?? '—'}</Text>
                  </Box>
                  <Box>
                    <Text as="p" variant="bodySm" tone="subdued">
                      Product description ({DESCRIPTION_SOURCE_LABEL[tire.description_source ?? ''] ?? '—'})
                    </Text>
                    <Box background="bg-surface-secondary" padding="300" borderRadius="200">
                      <Text as="p" variant="bodyMd">{tire.product_description ?? '—'}</Text>
                    </Box>
                  </Box>
                  <Box>
                    <Text as="p" variant="bodySm" tone="subdued">
                      Tags ({(tire.product_tags ?? []).length}) — actual tag values stay hyphenated for URL safety; we render the slash format here for readability only.
                    </Text>
                    <InlineStack gap="100" wrap>
                      {(tire.product_tags ?? []).map((t) => (
                        <Badge key={t}>{prettifyTag(t)}</Badge>
                      ))}
                    </InlineStack>
                  </Box>
                  <Box>
                    <Text as="p" variant="bodySm" tone="subdued">
                      Collection-rule tags ({(tire.collection_tags ?? []).length}) — drives smart-collection membership. Underlying values are unchanged.
                    </Text>
                    <InlineStack gap="100" wrap>
                      {(tire.collection_tags ?? []).map((t) => (
                        <Badge key={t} tone="info">{prettifyTag(t)}</Badge>
                      ))}
                    </InlineStack>
                  </Box>
                </BlockStack>
              </BlockStack>
            </Card>
          </Layout.Section>

          <Layout.Section>
            <ListingSeoPanel kind="tire" product={tire} />
          </Layout.Section>

          <Layout.Section>
            <GoogleMerchantPanel kind="tire" product={tire} />
          </Layout.Section>

          <Layout.Section>
            <MetafieldsPanel kind="tire" product={tire} />
          </Layout.Section>

          <Layout.Section>
            <VariantsPanel kind="tire" product={tire} />
          </Layout.Section>

          {/* Raw payload (dev / debug) ----------------------------------- */}
          {tire.raw_payload != null && (
            <Layout.Section>
              <Card>
                <BlockStack gap="200">
                  <Text as="h3" variant="headingMd">Raw vendor payload</Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Full ATD response for this SKU, preserved verbatim for debugging classification or pricing decisions. Read-only.
                  </Text>
                  <Box background="bg-surface-secondary" padding="300" borderRadius="200">
                    <pre style={{ margin: 0, fontSize: 11, maxHeight: 320, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
{JSON.stringify(tire.raw_payload, null, 2)}
                    </pre>
                  </Box>
                </BlockStack>
              </Card>
            </Layout.Section>
          )}
        </Layout>
        {toast && <Toast content={toast} onDismiss={() => setToast(null)} />}
      </Page>
    </>
  );
}

// Render-only beautifier for tag badges. Real tag VALUES (what gets sent
// to Shopify and used in collection rules) stay hyphenated for URL
// safety. This only swaps the size segment for display, e.g.
//   "size:195-65-15" → "size: 195/65-15"
//   "brand:bfgoodrich" → "brand: bfgoodrich"  (untouched, just spaced)
function prettifyTag(t: string): string {
  const i = t.indexOf(':');
  if (i < 0) return t;
  const key = t.slice(0, i);
  let val = t.slice(i + 1);
  if (key === 'size') {
    // Reuse the same display logic the rest of the UI uses.
    if (/^\d{2}(?:\.\d+)?x\d{1,2}(?:\.\d+)?r\d{2}$/i.test(val)) {
      val = val.toUpperCase();
    } else {
      const m = val.match(/^(p|lt|st)?(\d{3})-(\d{2})-(\d{2})$/i);
      if (m) val = `${(m[1] ?? '').toUpperCase()}${m[2]}/${m[3]}-${m[4]}`;
    }
  }
  return `${key}: ${val}`;
}

function InstallSpeedBadge({ speed }: { speed: TireProductFull['install_speed'] | null | undefined }) {
  if (!speed) return <Badge>—</Badge>;
  const tone =
    speed === 'same_day' ? 'success' :
    speed === 'next_day' ? 'info' :
    speed === 'out_of_stock' ? 'critical' :
    speed === 'estimated' ? 'attention' :
    'new';
  return <Badge tone={tone}>{INSTALL_LABEL[speed] ?? speed}</Badge>;
}
